import { apiErrorSchema } from '@plane/shared';
import type { ZodType } from 'zod';

/**
 * `import.meta.env` só existe dentro do Vite.
 *
 * O `?.` é o que permite importar este módulo fora dele — nos testes de
 * montagem, que rodam no Node puro. Sem isso o acesso estoura antes de qualquer
 * componente chegar a renderizar.
 */
const API_URL = import.meta.env?.VITE_API_URL ?? 'http://localhost:3333';

/** Falha vinda da API já traduzida para algo que a UI consegue exibir. */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

/**
 * Requisição à REST API com validação do corpo em runtime.
 *
 * O schema não é decoração: garante que uma mudança no contrato da API apareça
 * como erro claro aqui, e não como `undefined` no meio de um componente.
 */
export async function apiRequest<T>(
  path: string,
  schema: ZodType<T>,
  init?: RequestInit,
): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...init?.headers },
  });

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(payload);

    throw new ApiRequestError(
      response.status,
      parsed.success ? parsed.data.error.code : 'UNKNOWN_ERROR',
      parsed.success ? parsed.data.error.message : `Falha na requisição (${response.status}).`,
    );
  }

  return schema.parse(payload);
}

/** Base da API. As imagens são servidas por ela, não por este domínio. */
export const apiBaseUrl = API_URL;
